import { HANDOFF_ENABLED, RENDER_ORIGIN, PRIVATE_ORIGIN, HANDOFF_URL, LIMITS, nonce, isNonce, isId, exact, binding, receipt, notice, frame, isFrame, sameSession, clock, fail } from './handoff-protocol.mjs';

/**
 * Call start synchronously in the owner's liveCreate click handler, never after
 * an await. referrerPolicyReady is trusted host configuration: the Render page
 * MUST be served with Referrer-Policy: no-referrer. It is not browser detection.
 * getOwnerSession is an epoch guard, not server authorization. prepareNotice
 * must use same-origin owner-authenticated Render handling, bind both nonces,
 * retain requestId idempotency, and return {notice, binding}. No fetch fallback.
 */
export function createOwnerHandoff({
  enabled = HANDOFF_ENABLED, window, referrerPolicyReady = false, getOwnerSession,
  prepareNotice, randomNonce = nonce, now = Date.now,
  setInterval: schedule = globalThis.setInterval, clearInterval: unschedule = globalThis.clearInterval,
} = {}) {
  let active = null, completed = null;
  if (enabled === true && [getOwnerSession, prepareNotice, randomNonce, now, schedule, unschedule].some(fn => typeof fn !== 'function')) fail('ADAPTERS_REQUIRED');
  function stillOwner(attempt) {
    try { return sameSession(attempt.owner, getOwnerSession()); } catch { return false; }
  }
  function finish(attempt, status, extra = {}) {
    if (active !== attempt || attempt.done) return;
    attempt.done = true;
    window.removeEventListener('message', attempt.receive);
    unschedule(attempt.timer);
    active = null;
    const result = Object.freeze({ status, requestId: attempt.requestId, submission: attempt.sent ? 'unknown' : 'not-sent', ...extra });
    if (status === 'queued') completed = { requestId: attempt.requestId, owner: attempt.owner, result };
    attempt.resolve(result);
  }
  function guard(attempt) {
    if (active !== attempt || attempt.done) return false;
    if (!stillOwner(attempt)) { finish(attempt, 'account-changed'); return false; }
    if (clock(now()) >= attempt.deadline) { finish(attempt, 'timeout'); return false; }
    if (attempt.popup?.closed) { finish(attempt, 'popup-closed'); return false; }
    return true;
  }
  function post(attempt, message) {
    try { attempt.popup.postMessage(message, PRIVATE_ORIGIN); return true; }
    catch { finish(attempt, 'channel-lost'); return false; }
  }
  async function submit(attempt) {
    try {
      const envelope = await prepareNotice(Object.freeze({ requestId: attempt.requestId, ownerAccountId: attempt.owner.id,
        attemptNonce: attempt.nonce, pageNonce: attempt.pageNonce }));
      if (!guard(attempt)) return;
      if (!exact(envelope, ['notice', 'binding']) || !notice(envelope.notice) || !binding(envelope.binding)
        || envelope.binding.ownerAccountId !== attempt.owner.id || envelope.binding.requestId !== attempt.requestId
        || envelope.binding.attemptNonce !== attempt.nonce || envelope.binding.pageNonce !== attempt.pageNonce
        || envelope.binding.expiresAt <= clock(now()) || envelope.binding.expiresAt - now() > LIMITS.noticeLifetimeMs) fail('INVALID_NOTICE');
      attempt.binding = Object.freeze(structuredClone(envelope.binding));
      attempt.phase = 'submitted';
      // Conservatively mark uncertain before posting: a thrown browser transport
      // error is not evidence that the receiver/server never accepted the notice.
      attempt.sent = true;
      post(attempt, frame('submit', { attemptNonce: attempt.nonce, pageNonce: attempt.pageNonce, notice: envelope.notice, binding: attempt.binding }));
    } catch { if (active === attempt) finish(attempt, 'notice-failed'); }
  }
  function start({ requestId } = {}) {
    if (enabled !== true) fail('HANDOFF_DISABLED');
    if (!isId(requestId)) fail('INVALID_REQUEST_ID');
    if (active) {
      if (active.requestId === requestId) return active.promise;
      fail('HANDOFF_BUSY');
    }
    if (completed?.requestId === requestId) {
      if (!sameSession(completed.owner, getOwnerSession())) { completed = null; fail('OWNER_SESSION_CHANGED'); }
      return Promise.resolve(completed.result);
    }
    if (window?.location?.origin !== RENDER_ORIGIN || referrerPolicyReady !== true) fail('HOST_POLICY_REQUIRED');
    if (window?.navigator?.userActivation?.isActive !== true) fail('USER_GESTURE_REQUIRED');
    const owner = structuredClone(getOwnerSession());
    if (!sameSession(owner, owner)) fail('OWNER_SESSION_REQUIRED');
    const attemptNonce = randomNonce();
    if (!isNonce(attemptNonce)) fail('INVALID_NONCE');
    const attempt = { requestId, owner, nonce: attemptNonce, phase: 'opening', sent: false, done: false,
      deadline: clock(now()) + LIMITS.timeoutMs, popup: null };
    attempt.promise = new Promise(resolve => { attempt.resolve = resolve; });
    attempt.receive = event => {
      if (active !== attempt || event.origin !== PRIVATE_ORIGIN || !attempt.popup || event.source !== attempt.popup) return;
      if (!guard(attempt)) return;
      const data = event.data;
      if (isFrame(data, 'ready', ['pageNonce']) && isNonce(data.pageNonce) && ['opening', 'challenged'].includes(attempt.phase)) {
        if (attempt.pageNonce && attempt.pageNonce !== data.pageNonce) return;
        attempt.pageNonce = data.pageNonce; attempt.phase = 'challenged';
        post(attempt, frame('challenge', { attemptNonce: attempt.nonce, pageNonce: attempt.pageNonce }));
        return;
      }
      if (data?.attemptNonce !== attempt.nonce || data?.pageNonce !== attempt.pageNonce) return;
      if (isFrame(data, 'ack', ['attemptNonce', 'pageNonce']) && attempt.phase === 'challenged') {
        attempt.phase = 'preparing'; void submit(attempt); return;
      }
      if (isFrame(data, 'receipt', ['attemptNonce', 'pageNonce', 'receipt']) && attempt.phase === 'submitted'
        && receipt(data.receipt, attempt.binding)) {
        finish(attempt, 'queued', { submission: 'event-queued', receipt: Object.freeze(structuredClone(data.receipt)) });
      } else if (isFrame(data, 'error', ['attemptNonce', 'pageNonce', 'code']) && ['ACCOUNT_CHANGED', 'ACCEPTANCE_FAILED', 'REPLAY_REJECTED'].includes(data.code)) {
        finish(attempt, 'site-rejected', { code: data.code });
      }
    };
    active = attempt;
    window.addEventListener('message', attempt.receive);
    // Deliberately no query, fragment, user-supplied target, noopener or noreferrer.
    // The same fixed top-level URL is opened before any asynchronous operation.
    try { attempt.popup = window.open(HANDOFF_URL, '_blank', 'popup,width=520,height=640'); }
    catch { finish(attempt, 'popup-blocked'); }
    if (active === attempt && !attempt.popup) finish(attempt, 'popup-blocked');
    if (active === attempt) attempt.timer = schedule(() => guard(attempt), 250);
    return attempt.promise;
  }
  return Object.freeze({ start, cancel() { if (active) finish(active, 'cancelled'); },
    inspect() { return active ? { requestId: active.requestId, phase: active.phase, submission: active.sent ? 'unknown' : 'not-sent' } : null; } });
}
